import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SaveIcon from '@mui/icons-material/Save'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
  FormDefinition,
  FormField,
  useCreateForm,
  useForm,
  useUpdateForm,
} from 'api/aws/form-builder/useFormBuilder'
import {
  useClickUpSpaces,
  useClickUpFolders,
  useClickUpLists,
  useClickUpMembers,
} from 'api/aws/strategy-builder/useStrategyBuilder'

// ── Field type options ───────────────────────────────────────────────────────

const FIELD_TYPES: { value: FormField['type']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Select' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'checkbox', label: 'Checkbox' },
]

const STATUS_OPTIONS: { value: FormDefinition['status']; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
]

const newField = (): FormField => ({
  fieldId: uuidv4(),
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  options: [],
  defaultValue: '',
  helpText: '',
})

// ── Component ────────────────────────────────────────────────────────────────

interface FormEditorProps {
  formId: string | null
  open: boolean
  onClose: () => void
}

const FormEditor = ({ formId, open, onClose }: FormEditorProps) => {
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()

  const { data: existingForm } = useForm(formId)
  const createForm = useCreateForm()
  const updateForm = useUpdateForm()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<FormDefinition['status']>('draft')
  const [fields, setFields] = useState<FormField[]>([])
  const [expandedField, setExpandedField] = useState<string | false>(false)
  const [optionsInput, setOptionsInput] = useState<Record<string, string>>({})

  // ClickUp integration state
  const [clickupEnabled, setClickupEnabled] = useState(false)
  const [clickupListRouting, setClickupListRouting] = useState<'direct' | 'pattern'>('direct')
  const [clickupTargetListId, setClickupTargetListId] = useState('')
  const [clickupTargetListName, setClickupTargetListName] = useState('')
  const [clickupListPattern, setClickupListPattern] = useState('')
  const [clickupAssigneeName, setClickupAssigneeName] = useState('')
  const [clickupAssigneeId, setClickupAssigneeId] = useState('')
  // ClickUp browser state for direct list selection
  const [clickupSpaceId, setClickupSpaceId] = useState<string | null>(null)
  const [clickupFolderId, setClickupFolderId] = useState<string | null>(null)
  const [deliveryOnly, setDeliveryOnly] = useState(false)
  // ClickUp data hooks
  const spacesQuery = useClickUpSpaces()
  const foldersQuery = useClickUpFolders(clickupSpaceId)
  const listsQuery = useClickUpLists(clickupFolderId)
  const membersQuery = useClickUpMembers(clickupSpaceId)
  const filteredSpaces = (spacesQuery.data || []).filter(
    (s: any) => !deliveryOnly || s.name?.toUpperCase().includes('DELIVERY')
  )
  const [descriptionTemplate, setDescriptionTemplate] = useState('')
  const [completionMessage, setCompletionMessage] = useState('')

  // ── Load existing form ───────────────────────────────────────────────────

  useEffect(() => {
    if (existingForm) {
      setName(existingForm.name || '')
      setDescription(existingForm.description || '')
      setStatus(existingForm.status || 'draft')
      setFields(existingForm.fields || [])
      // ClickUp integration
      setClickupEnabled(existingForm.clickupEnabled || false)
      setClickupTargetListId(existingForm.clickupTargetListId || '')
      setClickupTargetListName(existingForm.clickupTargetListName || '')
      setClickupListPattern(existingForm.clickupListPattern || '')
      setClickupAssigneeName(existingForm.clickupAssigneeName || '')
      setClickupAssigneeId(existingForm.clickupAssigneeId != null ? String(existingForm.clickupAssigneeId) : '')
      setDescriptionTemplate(existingForm.descriptionTemplate || '')
      setCompletionMessage(existingForm.completionMessage || '')
      // Determine routing mode
      if (existingForm.clickupListPattern) {
        setClickupListRouting('pattern')
      } else {
        setClickupListRouting('direct')
      }
    }
  }, [existingForm])

  // ── Field operations ─────────────────────────────────────────────────────

  const addField = useCallback(() => {
    const f = newField()
    setFields(prev => [...prev, f])
    setExpandedField(f.fieldId)
  }, [])

  const removeField = useCallback((fieldId: string) => {
    setFields(prev => prev.filter(f => f.fieldId !== fieldId))
  }, [])

  const moveField = useCallback((index: number, direction: 'up' | 'down') => {
    setFields(prev => {
      const next = [...prev]
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }, [])

  const updateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    setFields(prev => prev.map(f => (f.fieldId === fieldId ? { ...f, ...updates } : f)))
  }, [])

  const handleAddOption = useCallback(
    (fieldId: string) => {
      const value = optionsInput[fieldId]?.trim()
      if (!value) return
      setFields(prev =>
        prev.map(f =>
          f.fieldId === fieldId ? { ...f, options: [...(f.options || []), value] } : f
        )
      )
      setOptionsInput(prev => ({ ...prev, [fieldId]: '' }))
    },
    [optionsInput]
  )

  const handleRemoveOption = useCallback((fieldId: string, optIndex: number) => {
    setFields(prev =>
      prev.map(f =>
        f.fieldId === fieldId
          ? { ...f, options: (f.options || []).filter((_, i) => i !== optIndex) }
          : f
      )
    )
  }, [])

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      showSnackbar('Form name is required', 'error')
      return
    }

    const clickupData = {
      clickupEnabled,
      clickupTargetListId: clickupListRouting === 'direct' ? clickupTargetListId : undefined,
      clickupTargetListName: clickupListRouting === 'direct' ? clickupTargetListName : undefined,
      clickupListPattern: clickupListRouting === 'pattern' ? clickupListPattern : undefined,
      clickupAssigneeName,
      clickupAssigneeId: clickupAssigneeId ? Number(clickupAssigneeId) : undefined,
      descriptionTemplate,
      completionMessage,
    }

    try {
      if (formId && existingForm) {
        await updateForm.mutateAsync({ id: formId, name, description, status, fields, ...clickupData })
      } else {
        await createForm.mutateAsync({ name, description, fields, status, ...clickupData } as any)
      }
      showSnackbar(formId ? 'Form updated' : 'Form created', 'success')
      onClose()
    } catch {
      showSnackbar('Failed to save form', 'error')
    }
  }, [
    name, description, status, fields, formId, existingForm, createForm, updateForm, showSnackbar, onClose,
    clickupEnabled, clickupListRouting, clickupTargetListId, clickupTargetListName, clickupListPattern,
    clickupAssigneeName, clickupAssigneeId, descriptionTemplate, completionMessage,
  ])

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {formId ? 'Edit Form' : 'New Form'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflow: 'auto' }}>
        <Grid container spacing={3}>
          {/* Left: Editor */}
          <Grid item xs={12} md={7}>
            {/* Form metadata */}
            <Stack spacing={2} mb={3}>
              <TextField
                label="Form Name"
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth
                required
                size="small"
              />
              <TextField
                label="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                size="small"
              />
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={e => setStatus(e.target.value as FormDefinition['status'])}
                >
                  {STATUS_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Fields */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Fields ({fields.length})
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addField}>
                Add Field
              </Button>
            </Stack>

            {fields.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <Typography color="text.secondary" mb={1}>
                  No fields yet
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addField}>
                  Add your first field
                </Button>
              </Paper>
            ) : (
              <Stack spacing={1}>
                {fields.map((field, index) => (
                  <Accordion
                    key={field.fieldId}
                    expanded={expandedField === field.fieldId}
                    onChange={(_, isExpanded) => setExpandedField(isExpanded ? field.fieldId : false)}
                    variant="outlined"
                    disableGutters
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={1} flex={1} mr={1}>
                        <Typography fontWeight={500} sx={{ flex: 1 }}>
                          {field.label || 'Untitled Field'}
                        </Typography>
                        <Chip label={field.type} size="small" variant="outlined" />
                        {field.required && <Chip label="Required" size="small" color="error" variant="outlined" />}
                        <Box onClick={e => e.stopPropagation()}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton size="small" disabled={index === 0} onClick={() => moveField(index, 'up')}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === fields.length - 1}
                                onClick={() => moveField(index, 'down')}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => removeField(field.fieldId)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            label="Label"
                            value={field.label}
                            onChange={e => updateField(field.fieldId, { label: e.target.value })}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                              value={field.type}
                              label="Type"
                              onChange={e => updateField(field.fieldId, { type: e.target.value as FormField['type'] })}
                            >
                              {FIELD_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.required}
                                onChange={e => updateField(field.fieldId, { required: e.target.checked })}
                                size="small"
                              />
                            }
                            label="Required"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Placeholder"
                            value={field.placeholder || ''}
                            onChange={e => updateField(field.fieldId, { placeholder: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Help Text"
                            value={field.helpText || ''}
                            onChange={e => updateField(field.fieldId, { helpText: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            label="Default Value"
                            value={field.defaultValue || ''}
                            onChange={e => updateField(field.fieldId, { defaultValue: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </Grid>

                        {/* Options (for select/multiselect) */}
                        {(field.type === 'select' || field.type === 'multiselect') && (
                          <Grid item xs={12}>
                            <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                              Options
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                              {(field.options || []).map((opt, oi) => (
                                <Chip
                                  key={oi}
                                  label={opt}
                                  size="small"
                                  onDelete={() => handleRemoveOption(field.fieldId, oi)}
                                  sx={{ mb: 0.5 }}
                                />
                              ))}
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <TextField
                                size="small"
                                placeholder="Add option..."
                                value={optionsInput[field.fieldId] || ''}
                                onChange={e =>
                                  setOptionsInput(prev => ({ ...prev, [field.fieldId]: e.target.value }))
                                }
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    handleAddOption(field.fieldId)
                                  }
                                }}
                                sx={{ flex: 1 }}
                              />
                              <Button size="small" onClick={() => handleAddOption(field.fieldId)}>
                                Add
                              </Button>
                            </Stack>
                          </Grid>
                        )}

                        {/* ── Per-Field ClickUp Mapping ──────────────────── */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                            ClickUp Mapping
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>ClickUp Mapping</InputLabel>
                            <Select
                              value={field.clickupMapping || 'none'}
                              label="ClickUp Mapping"
                              onChange={e =>
                                updateField(field.fieldId, {
                                  clickupMapping: e.target.value as FormField['clickupMapping'],
                                })
                              }
                            >
                              <MenuItem value="none">None</MenuItem>
                              <MenuItem value="description">Include in Description</MenuItem>
                              {field.type === 'date' && <MenuItem value="due_date">Due Date</MenuItem>}
                              <MenuItem value="custom_field">Custom Field</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {field.clickupMapping === 'custom_field' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="ClickUp Field ID"
                                value={field.clickUpFieldId || ''}
                                onChange={e => updateField(field.fieldId, { clickUpFieldId: e.target.value })}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>ClickUp Field Type</InputLabel>
                                <Select
                                  value={field.clickupFieldType || 'text'}
                                  label="ClickUp Field Type"
                                  onChange={e =>
                                    updateField(field.fieldId, { clickupFieldType: e.target.value as string })
                                  }
                                >
                                  <MenuItem value="text">text</MenuItem>
                                  <MenuItem value="drop_down">drop_down</MenuItem>
                                  <MenuItem value="labels">labels</MenuItem>
                                  <MenuItem value="number">number</MenuItem>
                                  <MenuItem value="currency">currency</MenuItem>
                                  <MenuItem value="date">date</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            {/* Option ID Mapping for select/multiselect with options */}
                            {(field.type === 'select' || field.type === 'multiselect') &&
                              (field.options || []).length > 0 && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">
                                    Option ID Mapping
                                  </Typography>
                                  <Stack spacing={1}>
                                    {(field.options || []).map((opt, oi) => {
                                      const existingMapping = (field.clickupOptions || []).find(
                                        co => co.value === opt
                                      )
                                      return (
                                        <Stack key={oi} direction="row" spacing={1} alignItems="center">
                                          <Typography variant="body2" sx={{ minWidth: 120 }}>
                                            {opt}
                                          </Typography>
                                          <TextField
                                            size="small"
                                            placeholder="ClickUp Option ID"
                                            value={existingMapping?.clickupOptionId || ''}
                                            onChange={e => {
                                              const updatedOptions = [...(field.clickupOptions || [])]
                                              const idx = updatedOptions.findIndex(co => co.value === opt)
                                              if (idx >= 0) {
                                                updatedOptions[idx] = {
                                                  ...updatedOptions[idx],
                                                  clickupOptionId: e.target.value,
                                                }
                                              } else {
                                                updatedOptions.push({
                                                  label: opt,
                                                  value: opt,
                                                  clickupOptionId: e.target.value,
                                                })
                                              }
                                              updateField(field.fieldId, { clickupOptions: updatedOptions })
                                            }}
                                            sx={{ flex: 1 }}
                                          />
                                        </Stack>
                                      )
                                    })}
                                  </Stack>
                                </Grid>
                              )}
                          </>
                        )}

                        {/* ── Conditional Visibility ─────────────────────── */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!field.conditionalRule}
                                onChange={e => {
                                  if (e.target.checked) {
                                    updateField(field.fieldId, {
                                      conditionalRule: {
                                        sourceFieldKey: '',
                                        operator: 'equals',
                                        value: '',
                                        action: 'show',
                                      },
                                    })
                                  } else {
                                    updateField(field.fieldId, { conditionalRule: undefined })
                                  }
                                }}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="caption" fontWeight={600}>
                                Conditional Visibility
                              </Typography>
                            }
                          />
                        </Grid>

                        {field.conditionalRule && (
                          <>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>When field</InputLabel>
                                <Select
                                  value={field.conditionalRule.sourceFieldKey}
                                  label="When field"
                                  onChange={e =>
                                    updateField(field.fieldId, {
                                      conditionalRule: {
                                        ...field.conditionalRule!,
                                        sourceFieldKey: e.target.value as string,
                                      },
                                    })
                                  }
                                >
                                  {fields
                                    .filter(f => f.fieldId !== field.fieldId)
                                    .map(f => (
                                      <MenuItem key={f.fieldId} value={f.fieldId}>
                                        {f.label || 'Untitled'}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Operator</InputLabel>
                                <Select
                                  value={field.conditionalRule.operator}
                                  label="Operator"
                                  onChange={e =>
                                    updateField(field.fieldId, {
                                      conditionalRule: {
                                        ...field.conditionalRule!,
                                        operator: e.target.value as FormField['conditionalRule']['operator'],
                                      },
                                    })
                                  }
                                >
                                  <MenuItem value="equals">equals</MenuItem>
                                  <MenuItem value="not_equals">not_equals</MenuItem>
                                  <MenuItem value="contains">contains</MenuItem>
                                  <MenuItem value="not_empty">not_empty</MenuItem>
                                  <MenuItem value="is_empty">is_empty</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Action</InputLabel>
                                <Select
                                  value={field.conditionalRule.action}
                                  label="Action"
                                  onChange={e =>
                                    updateField(field.fieldId, {
                                      conditionalRule: {
                                        ...field.conditionalRule!,
                                        action: e.target.value as 'show' | 'hide',
                                      },
                                    })
                                  }
                                >
                                  <MenuItem value="show">show</MenuItem>
                                  <MenuItem value="hide">hide</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {['equals', 'not_equals', 'contains'].includes(
                              field.conditionalRule.operator
                            ) && (
                              <Grid item xs={6}>
                                <TextField
                                  label="Value"
                                  value={field.conditionalRule.value || ''}
                                  onChange={e =>
                                    updateField(field.fieldId, {
                                      conditionalRule: {
                                        ...field.conditionalRule!,
                                        value: e.target.value,
                                      },
                                    })
                                  }
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
            {/* ── ClickUp Integration Settings ──────────────────────────── */}
            <Accordion sx={{ mt: 3 }} variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>ClickUp Integration</Typography>
                  {clickupEnabled && (
                    <Chip label="Enabled" size="small" color="success" variant="outlined" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clickupEnabled}
                        onChange={e => {
                          setClickupEnabled(e.target.checked)
                          if (e.target.checked && !spacesQuery.data) spacesQuery.refetch()
                        }}
                        size="small"
                      />
                    }
                    label="Enable ClickUp Integration"
                  />

                  {clickupEnabled && (
                    <>
                      {/* Routing mode */}
                      <Box>
                        <Typography variant="caption" fontWeight={600} gutterBottom display="block" color="text.secondary">
                          Task Routing
                        </Typography>
                        <RadioGroup
                          value={clickupListRouting}
                          onChange={e => setClickupListRouting(e.target.value as 'direct' | 'pattern')}
                          row
                        >
                          <FormControlLabel value="direct" control={<Radio size="small" />} label="Direct to specific list" />
                          <FormControlLabel value="pattern" control={<Radio size="small" />} label="User selects client (pattern match)" />
                        </RadioGroup>
                      </Box>

                      {clickupListRouting === 'direct' ? (
                        <>
                          {/* Step 1: Select Client — dropdown selectors */}
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                              <Typography variant="subtitle2" fontWeight={600}>Select Target List</Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={deliveryOnly}
                                    onChange={e => setDeliveryOnly(e.target.checked)}
                                  />
                                }
                                label={<Typography variant="caption">Delivery spaces only</Typography>}
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Group (Space)</InputLabel>
                                  <Select
                                    value={clickupSpaceId || ''}
                                    label="Group (Space)"
                                    onChange={e => {
                                      const val = e.target.value || null
                                      setClickupSpaceId(val)
                                      setClickupFolderId(null)
                                      setClickupTargetListId('')
                                      setClickupTargetListName('')
                                    }}
                                  >
                                    {filteredSpaces.map((s: any) => (
                                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={4}>
                                <FormControl fullWidth size="small" disabled={!clickupSpaceId}>
                                  <InputLabel>Client (Folder)</InputLabel>
                                  <Select
                                    value={clickupFolderId || ''}
                                    label="Client (Folder)"
                                    onChange={e => {
                                      const val = e.target.value || null
                                      setClickupFolderId(val)
                                      setClickupTargetListId('')
                                      setClickupTargetListName('')
                                    }}
                                  >
                                    {foldersQuery.isLoading && <MenuItem disabled>Loading...</MenuItem>}
                                    {(foldersQuery.data || []).map((f: any) => (
                                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={4}>
                                <FormControl fullWidth size="small" disabled={!clickupFolderId}>
                                  <InputLabel>List</InputLabel>
                                  <Select
                                    value={clickupTargetListId}
                                    label="List"
                                    onChange={e => {
                                      const listId = e.target.value
                                      setClickupTargetListId(listId)
                                      const list = (listsQuery.data || []).find((l: any) => l.id === listId)
                                      setClickupTargetListName(list?.name || '')
                                    }}
                                  >
                                    {listsQuery.isLoading && <MenuItem disabled>Loading...</MenuItem>}
                                    {(listsQuery.data || []).map((l: any) => (
                                      <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                            {clickupTargetListId && (
                              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                                Target: {clickupTargetListName} ({clickupTargetListId})
                              </Typography>
                            )}
                          </Paper>
                        </>
                      ) : (
                        <TextField
                          label="List Pattern"
                          value={clickupListPattern}
                          onChange={e => setClickupListPattern(e.target.value)}
                          fullWidth
                          size="small"
                          placeholder='e.g., "| creative" — will match client lists containing this pattern'
                          helperText="Users will select a space and folder; the system will auto-match the list by this pattern"
                        />
                      )}

                      {/* Default Assignee */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Default Assignee</InputLabel>
                        <Select
                          value={clickupAssigneeId}
                          label="Default Assignee"
                          onChange={e => {
                            const id = e.target.value
                            setClickupAssigneeId(id)
                            const member = (membersQuery.data || []).find((m: any) => String(m.id) === String(id))
                            setClickupAssigneeName(member?.username || member?.email || '')
                          }}
                        >
                          <MenuItem value="">
                            <em>No default assignee</em>
                          </MenuItem>
                          {(membersQuery.data || []).map((m: any) => (
                            <MenuItem key={m.id} value={String(m.id)}>
                              {m.username || m.email} {m.email && m.username ? `(${m.email})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        label="Description Template"
                        value={descriptionTemplate}
                        onChange={e => setDescriptionTemplate(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        helperText="Use {{details}} for field values and {{description}} for user comments"
                      />

                      <TextField
                        label="Completion Message"
                        value={completionMessage}
                        onChange={e => setCompletionMessage(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Custom message shown after successful submission"
                      />
                    </>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Right: Preview */}
          <Grid item xs={12} md={5}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                position: 'sticky',
                top: 0,
                bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" mb={2} textTransform="uppercase" letterSpacing={1}>
                Preview
              </Typography>
              <Typography variant="h6" fontWeight={600} mb={0.5}>
                {name || 'Untitled Form'}
              </Typography>
              {description && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {description}
                </Typography>
              )}
              <Divider sx={{ mb: 2 }} />

              {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  Add fields to see preview
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {fields.map(field => (
                    <Box key={field.fieldId}>
                      {field.type === 'checkbox' ? (
                        <FormControlLabel
                          control={<Switch size="small" disabled />}
                          label={
                            <Typography variant="body2">
                              {field.label || 'Untitled'} {field.required && '*'}
                            </Typography>
                          }
                        />
                      ) : field.type === 'select' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {field.label || 'Untitled'} {field.required && '*'}
                          </InputLabel>
                          <Select label={`${field.label || 'Untitled'} ${field.required ? '*' : ''}`} disabled>
                            {(field.options || []).map(opt => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                          {field.helpText && (
                            <Typography variant="caption" color="text.secondary">
                              {field.helpText}
                            </Typography>
                          )}
                        </FormControl>
                      ) : field.type === 'textarea' ? (
                        <TextField
                          label={`${field.label || 'Untitled'} ${field.required ? '*' : ''}`}
                          placeholder={field.placeholder}
                          helperText={field.helpText}
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          disabled
                        />
                      ) : (
                        <TextField
                          label={`${field.label || 'Untitled'} ${field.required ? '*' : ''}`}
                          placeholder={field.placeholder}
                          helperText={field.helpText}
                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          fullWidth
                          size="small"
                          disabled
                          InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                        />
                      )}
                    </Box>
                  ))}
                  <Button variant="contained" disabled fullWidth>
                    Submit
                  </Button>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={createForm.isPending || updateForm.isPending}
        >
          {formId ? 'Save Changes' : 'Create Form'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FormEditor
